import React from 'react';
import { Star, Calendar, ExternalLink } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { AppState, ModuleId } from '../../types';
import { GroupId } from '../../config/systemConfig';
import { MODULES } from '../../data/questionLoader';
import { getWeeklyActivityForLearner } from '../../utils/activityHelpers';
import { WeeklyActivityDots } from '../shared/WeeklyActivityDots';
interface ChildSummaryCardProps {
  childName: string;
  learnerData: AppState;
  selectedGroup: GroupId;
  getModuleMastery: (moduleId: ModuleId) => number;
  onViewDashboard?: () => void;
}
export function ChildSummaryCard({
  childName,
  learnerData,
  selectedGroup,
  getModuleMastery,
  onViewDashboard
}: ChildSummaryCardProps) {
  const totalMastery = MODULES.reduce((sum, m) => sum + getModuleMastery(m.id), 0);
  const avgMastery = Math.round(totalMastery / MODULES.length);
  // 5 dots for overall mastery (each dot = 20%)
  const filledDots = Math.ceil(avgMastery / 20);
  // Real weekly activity derived from questionStates timestamps
  const weeklyActivity = getWeeklyActivityForLearner(learnerData.questionStates || {});
  return <Card className="p-6 bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-100" data-id="element-4299">
      <div className="flex items-start justify-between mb-6" data-id="element-4300">
        <div className="flex items-center gap-5" data-id="element-4301">
          <div className="w-20 h-20 rounded-full bg-white border-4 border-emerald-200 shadow-md flex items-center justify-center text-3xl font-black text-emerald-600" data-id="element-4302">
            {childName.charAt(0).toUpperCase()}
          </div>
          <div data-id="element-4303">
            <h3 className="text-2xl font-black text-slate-900 mb-1" data-id="element-4304">
              {childName}
            </h3>
            <div className="flex items-center gap-1 text-amber-500" data-id="element-4305">
              {Array.from({
              length: Math.min(5, learnerData.stats.level)
            }).map((_, i) => <Star key={i} className="w-5 h-5 fill-current" data-id="element-4306" />)}
              <span className="text-sm font-bold text-slate-600 ml-2" data-id="element-4307">
                Level {learnerData.stats.level}
              </span>
            </div>
          </div>
        </div>

        {onViewDashboard && <Button variant="ghost" size="sm" onClick={onViewDashboard} className="text-emerald-700 hover:bg-emerald-100" data-id="element-4308">
            <ExternalLink className="w-4 h-4 mr-2" data-id="element-4309" />
            Bekijk dashboard
          </Button>}
      </div>

      <div className="space-y-6" data-id="element-4310">
        <div data-id="element-4311">
          <div className="flex justify-between items-center mb-2" data-id="element-4312">
            <span className="text-sm font-bold text-slate-700" data-id="element-4313">
              Algemene Voortgang
            </span>
            <span className="text-sm font-bold text-emerald-600" data-id="element-4314">
              {avgMastery >= 80 ? 'Geweldig!' : avgMastery >= 40 ? 'Goed bezig' : 'Startend'}
            </span>
          </div>
          <div className="flex gap-2" data-id="element-4315">
            {Array.from({
            length: 5
          }).map((_, i) => <div key={i} className={`flex-1 h-3 rounded-full ${i < filledDots ? 'bg-emerald-500' : 'bg-emerald-200/50'}`} data-id="element-4316" />)}
          </div>
        </div>

        <div className="pt-4 border-t border-emerald-100" data-id="element-4317">
          <div className="flex items-center gap-2 mb-3" data-id="element-4318">
            <Calendar className="w-4 h-4 text-emerald-600" data-id="element-4319" />
            <span className="text-sm font-bold text-slate-700" data-id="element-4320">
              Deze week geoefend
            </span>
          </div>
          <WeeklyActivityDots weeklyActivity={weeklyActivity} color="emerald" data-id="element-4321" />
        </div>
      </div>
    </Card>;
}